import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { TrendingUp, TrendingDown, AlertTriangle, Target, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";

import { AnimatedPage, AnimatedCard, AnimatedCounter, SectionHeader } from "../BizDevAnimations";
import "../BizDevAnimations.css";
import { useCanViewFinancials } from "../hooks/useCanViewFinancials";

import { useCanViewFunnel } from "../hooks/useCanViewFunnel";

const STAGE_COLORS = {
  Cold: "#64748B", Warm: "#F59E0B", Hot: "#EF4444", Qualified: "#8B5CF6",
  "RFP Received": "#06B6D4", "Proposal Sent": "#3B82F6", "Follow-up": "#F97316",
  Negotiation: "#EC4899", Won: "#22C55E", Lost: "#6B7280",
};

const formatCr = (lakhs) => `₹${(lakhs / 100).toFixed(1)}Cr`;
const formatLakhs = (v) => v >= 100 ? `₹${(v / 100).toFixed(1)}Cr` : `₹${v.toFixed(0)}L`;

export default function BizDevFunnel() {
  const canViewFunnel = useCanViewFunnel();
  const canViewFinancials = useCanViewFinancials();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  const loadData = async () => {
    setRefreshing(true);
    try { setData((await api.get("/funnel/stats")).data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { loadData(); }, []);

  if (!canViewFunnel) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh", flexDirection: "column", gap: "16px" }}>
        <span style={{ fontSize: "48px" }}>🔒</span>
        <h2 style={{ color: "var(--t1)", fontFamily: "var(--font-orb)" }}>Access Restricted</h2>
        <p style={{ color: "var(--t3)", fontFamily: "var(--font-mono)", fontSize: "13px" }}>You don{"'"}t have permission to view funnel data. Contact an admin.</p>
      </div>
    );
  }

  if (loading) return <><div className="flex items-center justify-center h-64"><div className="animate-pulse text-cyan-400 font-heading text-xl">LOADING...</div></div></>;

  const stages = data?.stages || [];
  const ins = data?.insights || {};
  const onTrack = ins.on_track;
  const totalRevenueLakhs = stages.reduce((s, st) => s + (st.won_lakhs || 0), 0);
  const totalPipelineLakhs = ins.total_pipeline_lakhs || 0;

  return (
    <>
      <div data-testid="bizdev-funnel" className="space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-2xl sm:text-3xl text-[var(--t1)] tracking-tight">SALES FUNNEL</h1>
          <button onClick={loadData} className="p-2 text-text-muted hover:text-cyan-400 rounded-xl transition-colors"><RefreshCw size={18} className={refreshing ? "animate-spin" : ""} /></button>
        </div>

        {/* Behind/On Target Card */}
        <div className={`glass-card rounded-2xl p-5 ${onTrack ? "border-green-500/30" : "border-yellow-500/30"}`}
          style={{ borderColor: onTrack ? "rgba(34,197,94,0.3)" : "rgba(234,179,8,0.3)" }}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-white font-bold text-lg normal-case flex items-center gap-2">
                {onTrack ? <TrendingUp size={18} className="text-green-400" /> : <TrendingDown size={18} className="text-yellow-400" />}
                {onTrack ? "On Track" : "Behind Target"}
              </h2>
              <p className="text-[var(--t2)] text-sm normal-case">Probability of hitting ₹50 Cr by Dec 2026</p>
            </div>
            <span className={`text-4xl font-heading ${onTrack ? "text-green-400" : "text-white"}`}>{ins.probability_percent || 0}%</span>
          </div>
          {/* Progress bar */}
          <div className="h-2 bg-[var(--s3)] rounded-full overflow-hidden mb-4">
            <div className="h-full rounded-full transition-all duration-1000"
              style={{ width: `${Math.min(ins.probability_percent || 0, 100)}%`, background: onTrack ? "#22C55E" : ins.probability_percent > 40 ? "#EAB308" : "#E31937" }} />
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div><p className="text-white font-bold text-lg">{formatCr(ins.projected_eoy_lakhs || 0)}</p><p className="text-[var(--t3)] text-xs">Projected EOY</p></div>
            <div><p className="text-white font-bold text-lg">{formatCr(ins.target_remaining_lakhs || 0)}</p><p className="text-[var(--t3)] text-xs">Remaining</p></div>
            <div><p className="text-white font-bold text-lg">₹{(ins.monthly_run_rate_lakhs || 0).toFixed(1)}L/mo</p><p className="text-[var(--t3)] text-xs">Run Rate</p></div>
          </div>
          {ins.briefs_needed > 0 && (
            <div className="mt-4 px-4 py-2.5 rounded-xl bg-yellow-500/10 border border-yellow-500/20 text-center">
              <p className="text-yellow-400 text-sm font-medium normal-case">Need ~{ins.briefs_needed} new briefs (₹25L avg) to close the gap</p>
            </div>
          )}
        </div>

        {/* AI Recommended Actions */}
        {ins.actions?.length > 0 && (
          <div className="glass-card rounded-2xl p-5">
            <h3 className="text-white font-bold text-base mb-3 normal-case">AI Recommended Actions</h3>
            <div className="space-y-2">
              {ins.actions.map((a, i) => (
                <button key={i} onClick={() => a.lead_id && navigate(`/bizdev/leads/${a.lead_id}`)}
                  className="w-full text-left flex items-center gap-3 p-2.5 rounded-xl hover:bg-[var(--s2)] transition-colors">
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${a.priority === "critical" ? "bg-red-500" : "bg-yellow-500"}`} />
                  <span className="text-[var(--t2)] text-sm normal-case">{a.text}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Pipeline vs Revenue Split */}
        <div className="grid grid-cols-2 gap-3">
          <div className="glass-card rounded-2xl p-5 text-center">
            <p className="text-white font-heading text-2xl">{formatLakhs(totalPipelineLakhs)}</p>
            <p className="text-[var(--t3)] text-xs">Active Pipeline</p>
          </div>
          <div className="glass-card rounded-2xl p-5 text-center">
            <p className="text-green-400 font-heading text-2xl">{canViewFinancials ? formatCr(totalRevenueLakhs) : "---"}</p>
            <p className="text-[var(--t3)] text-xs">Total Revenue</p>
          </div>
        </div>

        {/* Stage Breakdown */}
        <div className="space-y-2">
          {stages.filter((s) => s.count > 0 || ["Cold", "Warm", "Hot", "Won", "Lost"].includes(s.stage)).map((stage) => {
            const isExpanded = expanded[stage.stage];
            const totalAllRevenue = totalRevenueLakhs || 1;
            const pct = totalAllRevenue > 0 ? Math.round((stage.won_lakhs / totalAllRevenue) * 100) : 0;
            return (
              <div key={stage.stage}>
                <button onClick={() => stage.count > 0 && setExpanded({ ...expanded, [stage.stage]: !isExpanded })}
                  data-testid={`funnel-stage-${stage.stage.toLowerCase().replace(/\s/g, "-")}`}
                  className="w-full glass-card rounded-xl px-4 py-3.5 flex items-center justify-between transition-all hover:border-[var(--bdrh)]">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: STAGE_COLORS[stage.stage] || "#64748B" }} />
                    <span className="text-white font-semibold text-sm">{stage.stage}</span>
                    <span className="text-[var(--t3)] text-xs bg-[var(--s2)] px-2 py-0.5 rounded-full">{stage.count}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium" style={{ color: stage.stage === "Won" ? "#22C55E" : stage.stage === "Hot" ? "#EF4444" : stage.stage === "Negotiation" ? "#EC4899" : "#71717A" }}>
                      {formatCr(stage.total_lakhs || 0)}
                    </span>
                    <span className="text-[var(--t3)] text-xs w-10 text-right">{pct}%</span>
                    {stage.count > 0 && (isExpanded ? <ChevronUp size={14} className="text-[var(--t3)]" /> : <ChevronDown size={14} className="text-[var(--t3)]" />)}
                  </div>
                </button>
                {isExpanded && stage.leads?.length > 0 && (
                  <div className="ml-4 mt-1 space-y-1">
                    {stage.leads.map((l) => (
                      <button key={l.lead_id} onClick={() => navigate(`/bizdev/leads/${l.lead_id}`)}
                        className="w-full text-left flex items-center justify-between p-3 bg-white/[0.02] border border-white/5 rounded-xl hover:border-white/15 transition-colors text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-white">{l.company_name}</span>
                          {l.is_retainer && <span className="text-[12px] border border-cyan-500/40 text-cyan-400 px-1.5 rounded-full">R</span>}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-[var(--t3)]">
                          {l.total_lakhs > 0 && <span>{l.total_lakhs}L</span>}
                          {l.brief_count > 0 && <span>{l.brief_count} briefs</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
